/*
  MODIFICATION JUBYLEE — ordre des medias et regroupement par coloris

  1. Dawn deplacait physiquement le media de la variante en tete de
     galerie (prepend), vignette comprise, a chaque changement de
     variante. Ce deplacement est supprime : l'ordre de l'admin est
     desormais respecte en permanence.

  2. Filtrage par groupe. product-media-gallery.liquid pose un attribut
     data-jbl-group sur chaque media : une image rattachee a une variante
     ouvre un groupe, les suivantes en heritent jusqu'a la prochaine
     image de reference. On n'affiche que le groupe du media actif.
     Le groupe "all" reste toujours visible.
*/

if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        this.grouped = this.dataset.jblGroups === 'true';
        if (this.grouped) {
          const actif = this.elements.viewer.querySelector('[data-media-id].is-active');
          if (actif) this.filterGroup(actif.dataset.jblGroup);
        }

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      /* N'affiche que les medias du groupe demande, plus ceux du groupe
         "all" places avant toute image de reference. Ne touche jamais a
         l'ordre du DOM. */
      filterGroup(groupe) {
        if (!this.grouped || !groupe) return;

        const appliquer = (racine) => {
          if (!racine) return;
          racine.querySelectorAll('[data-jbl-group]').forEach((element) => {
            const sien = element.dataset.jblGroup;
            const visible = sien === groupe || sien === 'all';
            element.hidden = !visible;
            element.classList.toggle('jbl-media-hidden', !visible);
          });
        };

        appliquer(this.elements.viewer);
        appliquer(this.elements.thumbnails);

        this.currentGroup = groupe;
        this.refreshSliders();
      }

      refreshSliders() {
        [this.elements.viewer, this.elements.thumbnails].forEach((composant) => {
          if (composant && typeof composant.resetPages === 'function') {
            try {
              composant.resetPages();
            } catch (e) {
              /* le slider n'est pas encore initialise */
            }
          }
        });
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }

        /* Le groupe se cale avant l'affichage, sinon on rend actif un
           media que le filtre s'apprete a masquer. */
        if (this.grouped && activeMedia.dataset.jblGroup) {
          this.filterGroup(activeMedia.dataset.jblGroup);
        }

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        /* prepend volontairement ignore : il cassait l'ordre de l'admin.
           On se contente de reinitialiser la pagination du slider. */
        if (prepend && this.elements.viewer.slider) {
          this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        if (!activeThumbnail) return;
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
